import { useEffect, useState } from 'react';

/**
 * True once a query has been fetched afresh since `key` last became active.
 *
 * The archive dialogs ask what becomes of a project's boards (HW-983), and the
 * answer is applied by the server to the boards it holds now. A cached list may
 * be minutes old, so the question waits for a fresh one rather than trusting
 * the cache — and only for that one, not for every background refetch.
 * `key` is anything that names what is being asked about (a project id), or
 * null while the dialog is closed.
 */
export function useFreshOnOpen(key: string | null, refetch: () => Promise<unknown>): boolean {
  const [freshFor, setFreshFor] = useState<string | null>(null);

  useEffect(() => {
    // Closed: forget, so reopening on the same project asks again.
    if (key === null) {
      setFreshFor(null);
      return;
    }
    let live = true;
    void refetch().finally(() => {
      if (live) setFreshFor(key);
    });
    return () => {
      live = false;
    };
    // `refetch` is TanStack Query's and stable per query; re-running on it would
    // refetch on every render.
  }, [key]);

  return key !== null && freshFor === key;
}
